/**
 * Routine Surveillance Service
 *
 * Handles fetching routine surveillance data from the cross-account
 * Athena/Glue catalog via the API Gateway. The backend queries the
 * map_rs_data_snapshots table in the map-workbench account and returns
 * the results as JSON.
 *
 * Requires an authenticated user with a valid Auth0 access token.
 */

// Cached API base URL loaded from aws-exports.json
let API_BASE_URL = ""

/**
 * Load the routine-surveillance API base URL from aws-exports.json configuration.
 *
 * Uses VITE_API_URL env var if set, otherwise falls back to the apiUrl
 * from aws-exports.json, and finally to a relative path.
 *
 * @returns The base URL for the routine-surveillance API endpoint
 *          (e.g. "https://host/api/routine-surveillance").
 */
async function loadApiBaseUrl(): Promise<string> {
  if (API_BASE_URL) {
    return API_BASE_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = import.meta.env.VITE_API_URL || config.apiUrl || ""
    API_BASE_URL = baseUrl
      ? `${baseUrl}/api/routine-surveillance`
      : "/api/routine-surveillance"
    return API_BASE_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Routine Surveillance API URL not configured")
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Column filters keyed by column name. Each entry maps a filterable column
 * (e.g. "iso3") to the list of values to match. Multiple values for one column
 * are OR-ed by the backend (SQL IN), and different columns are AND-ed together.
 */
export type RoutineSurveillanceFilters = Record<string, string[]>

/**
 * The two topic partitions of the routine surveillance snapshot. Every query is
 * scoped to exactly one, so incidence and mortality rows are never mixed. Kept
 * as a union (rather than a bare string) so the compiler catches an invalid
 * topic before it reaches the backend, which would reject it with a 400.
 */
export type RoutineSurveillanceTopic = "incidence" | "mortality"

/**
 * The topic used when a caller does not specify one. Mirrors the backend's
 * DEFAULT_TOPIC so the frontend and backend agree on the default view.
 */
export const DEFAULT_TOPIC: RoutineSurveillanceTopic = "incidence"

/** Optional filter parameters for querying routine surveillance data. */
export interface GetRoutineSurveillanceParams {
  /**
   * Optional snapshot version to query (a partition value like "v1.3"). If
   * omitted, the backend automatically resolves the latest snapshot version.
   */
  snapshot_version?: string
  /**
   * Optional topic partition to query ("incidence" or "mortality"). If omitted,
   * the backend defaults to "incidence". Every query is constrained to one
   * topic so incidence and mortality rows are never returned together.
   */
  topic?: RoutineSurveillanceTopic
  /**
   * Optional include filters. Serialized as repeated `filter.<column>=v1,v2`
   * query parameters that the backend matches with SQL `IN`.
   */
  filters?: RoutineSurveillanceFilters
  /**
   * Optional exclude filters. Serialized as repeated `filterNot.<column>=v1,v2`
   * query parameters that the backend matches with SQL `NOT IN`. Used, for
   * example, to show subnational rows by excluding the national admin id.
   */
  excludeFilters?: RoutineSurveillanceFilters
}

/** Metadata returned alongside the query results. */
export interface RoutineSurveillanceMetadata {
  /** The Glue table that was queried. */
  table: string
  /** The snapshot version used (may be auto-resolved if not provided in the request). */
  snapshot_version: string | null
  /** The topic partition the rows were drawn from ("incidence" or "mortality"). */
  topic: RoutineSurveillanceTopic
  /** Athena query execution ID for debugging/tracing. */
  queryExecutionId: string
}

/** A single row of routine surveillance data from the map_rs_data_snapshots table. */
export interface RoutineSurveillanceRow {
  facility_type: string | null
  sector: string | null
  origin_name: string | null
  case_detection_name: string | null
  start_date: string | null
  end_date: string | null
  map_admin_id: string | null
  admin_unit_name: string | null
  country_name: string | null
  admin_level: string | null
  iso3: string | null
  pf: string | null
  pv: string | null
  po: string | null
  pm: string | null
  pk: string | null
  is_malignant: string | null
  is_mixed_infection: string | null
  is_tertian: string | null
  pregnant_only: string | null
  sex: string | null
  urbanisation: string | null
  lower_age_years: string | null
  upper_age_years: string | null
  metric_name: string | null
  value: string | null
  biblio_id: string | null
  manual_extraction_data_id: string | null
  document_extraction_fact_id: string | null
  series_id: string | null
  consolidated_species_code: string | null
}

/**
 * Paginated response shape from GET /api/routine-surveillance.
 *
 * Mirrors the journal articles paginated response so the RS data interface
 * can reuse the same lazy-loading (one page per request) pattern.
 */
export interface RoutineSurveillanceResponse {
  /** Array of row objects for the requested page. */
  items: RoutineSurveillanceRow[]
  /** Total number of rows in the partition across all pages. */
  totalCount: number
  /** The 1-based page number that was returned. */
  page: number
  /** The number of rows per page used for this response. */
  pageSize: number
  /** Total number of pages available for the current partition. */
  totalPages: number
  /** Query metadata including table, partition, and execution ID. */
  metadata: RoutineSurveillanceMetadata
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetch routine surveillance data from the cross-account Athena catalog.
 *
 * Queries the map_rs_data_snapshots table in the map-workbench account
 * via Athena, returning a single page of rows. If no snapshot_version is
 * specified, the backend automatically resolves and returns the latest
 * available snapshot version. If no topic is specified, the backend defaults
 * to "incidence".
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param page - 1-based page number to fetch (default 1).
 * @param pageSize - Number of rows per page (default 50, max 200).
 * @param params - Optional filter parameters (snapshot_version, topic, and
 *        column filters).
 * @returns The parsed paginated routine surveillance response containing the
 *          requested page of rows plus pagination metadata.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getRoutineSurveillanceData(
  accessToken: string,
  page: number = 1,
  pageSize: number = 50,
  params: GetRoutineSurveillanceParams = {}
): Promise<RoutineSurveillanceResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()

  // Pagination parameters — always included so the backend pages the results.
  searchParams.set("page", String(page))
  searchParams.set("pageSize", String(pageSize))

  // Optional snapshot version — only include if provided.
  if (params.snapshot_version !== undefined) {
    searchParams.set("snapshot_version", params.snapshot_version)
  }

  // Optional topic partition — only include if provided; the backend defaults
  // to "incidence" when it is omitted.
  if (params.topic !== undefined) {
    searchParams.set("topic", params.topic)
  }

  // Optional include filters — serialize each column as filter.<column>=v1,v2.
  // Columns with no selected values are skipped so we never send empty filters.
  if (params.filters !== undefined) {
    for (const [column, values] of Object.entries(params.filters)) {
      if (values.length > 0) {
        searchParams.set(`filter.${column}`, values.join(","))
      }
    }
  }

  // Optional exclude filters — serialize as filterNot.<column>=v1,v2 (SQL NOT IN).
  if (params.excludeFilters !== undefined) {
    for (const [column, values] of Object.entries(params.excludeFilters)) {
      if (values.length > 0) {
        searchParams.set(`filterNot.${column}`, values.join(","))
      }
    }
  }

  const url = `${baseUrl}?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    )
  }

  const data: RoutineSurveillanceResponse = await response.json()

  // Validate the response shape rather than trusting it blindly. The paginated
  // endpoint must return an `items` array; if it doesn't (e.g. the deployed
  // Lambda is an older, pre-pagination version still returning `data`), fail
  // loudly here with an actionable message instead of letting an undefined
  // `items` propagate and crash the table renderer downstream.
  if (!Array.isArray(data?.items)) {
    throw new Error(
      "Unexpected routine surveillance API response: missing 'items' array. " +
      "The API may be running an older version that predates paginated responses."
    )
  }

  return data
}

/** Response shape from GET /api/routine-surveillance/facets. */
export interface RoutineSurveillanceFacetResponse {
  /** The column the distinct values belong to. */
  column: string
  /** The snapshot version the values were drawn from. */
  snapshot_version: string | null
  /** The topic partition the values were drawn from ("incidence" or "mortality"). */
  topic: RoutineSurveillanceTopic
  /** The distinct non-null values available for the column, ordered ascending. */
  values: string[]
}

/**
 * Fetch the distinct selectable values for a single filterable column, scoped
 * to a topic.
 *
 * Used to populate the options in the table filter dialog and the graph's
 * metric dropdown so users choose from real dataset values rather than typing
 * free text. The backend scans the latest snapshot (or the given one) within
 * the given topic and returns the distinct non-null values for the column,
 * capped server-side. Scoping to the topic matters: the set of metrics (and
 * other facet values) differs between incidence and mortality, so the options
 * must reflect only the topic currently being viewed.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param column - The column to fetch distinct values for. Must be one of the
 *                 backend's filterable columns, or the request returns 400.
 * @param topic - Optional topic partition to scan; if omitted the backend
 *                defaults to "incidence".
 * @param snapshot_version - Optional snapshot version to scan; if omitted the
 *                      backend resolves the latest snapshot version.
 * @returns The facet response containing the column and its distinct values.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getRoutineSurveillanceFacets(
  accessToken: string,
  column: string,
  topic?: RoutineSurveillanceTopic,
  snapshot_version?: string
): Promise<RoutineSurveillanceFacetResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()

  searchParams.set("column", column)
  // Scope the facet to the topic when provided; the backend defaults to
  // "incidence" otherwise.
  if (topic !== undefined) {
    searchParams.set("topic", topic)
  }
  if (snapshot_version !== undefined) {
    searchParams.set("snapshot_version", snapshot_version)
  }

  const url = `${baseUrl}/facets?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    )
  }

  const data: RoutineSurveillanceFacetResponse = await response.json()

  // Validate the response shape rather than trusting it blindly — a malformed
  // response should fail loudly here instead of feeding undefined into the
  // filter dialog's option list.
  if (!Array.isArray(data?.values)) {
    throw new Error(
      "Unexpected routine surveillance facets response: missing 'values' array."
    )
  }

  return data
}

/** Optional parameters for the bulk (all-rows) routine surveillance query. */
export interface GetAllRoutineSurveillanceParams extends GetRoutineSurveillanceParams {
  /**
   * Optional projection: the columns to return. When provided, only these
   * columns are selected (and validated server-side against the allowlist),
   * which cuts the transferred payload for large result sets. When omitted, all
   * columns are returned. Each name must be a member of the backend's
   * SELECTABLE_COLUMNS or the request returns 400.
   */
  columns?: (keyof RoutineSurveillanceRow)[]
}

/** Response shape from GET /api/routine-surveillance/all. */
export interface RoutineSurveillanceBulkResponse {
  /** Every row matching the filters (up to the backend's row cap). */
  items: RoutineSurveillanceRow[]
  /** The number of rows returned (after any truncation). */
  rowCount: number
  /**
   * True when the result hit the backend's row cap and is therefore
   * incomplete. Callers should surface this rather than treating the partial
   * set as the full result.
   */
  truncated: boolean
  /** Query metadata including table, partition, and execution ID. */
  metadata: RoutineSurveillanceMetadata
}

/**
 * Fetch EVERY row matching a query in a single request via the bulk endpoint.
 *
 * Unlike getRoutineSurveillanceData (which pages), this hits
 * GET /api/routine-surveillance/all, where the backend runs ONE Athena query
 * with no per-page COUNT and no OFFSET paging — far cheaper than looping the
 * paged endpoint to assemble the same set. Pass `columns` to project only the
 * fields you need and shrink the payload.
 *
 * Intended for callers that genuinely need the full result set at once (e.g.
 * the graph view plotting every point). For the table's lazy, one-page-at-a-time
 * loading, use getRoutineSurveillanceData instead.
 *
 * The backend caps the result at a fixed number of rows as a safety backstop.
 * When that cap is hit the returned `truncated` flag is true and the data is
 * incomplete; this function surfaces the flag rather than hiding it, so callers
 * can decide how to react (warn the user, narrow the query, etc.).
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param params - Optional filters, exclude filters, partition, and column
 *        projection applied to the single query.
 * @returns The bulk response: all matching rows (up to the cap), the row count,
 *          a truncation flag, and query metadata.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getAllRoutineSurveillanceData(
  accessToken: string,
  params: GetAllRoutineSurveillanceParams = {}
): Promise<RoutineSurveillanceBulkResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()

  // Optional snapshot version — only include if provided.
  if (params.snapshot_version !== undefined) {
    searchParams.set("snapshot_version", params.snapshot_version)
  }

  // Optional topic partition — only include if provided; the backend defaults
  // to "incidence" when it is omitted.
  if (params.topic !== undefined) {
    searchParams.set("topic", params.topic)
  }

  // Optional include filters — serialize each column as filter.<column>=v1,v2.
  if (params.filters !== undefined) {
    for (const [column, values] of Object.entries(params.filters)) {
      if (values.length > 0) {
        searchParams.set(`filter.${column}`, values.join(","))
      }
    }
  }

  // Optional exclude filters — serialize as filterNot.<column>=v1,v2 (SQL NOT IN).
  if (params.excludeFilters !== undefined) {
    for (const [column, values] of Object.entries(params.excludeFilters)) {
      if (values.length > 0) {
        searchParams.set(`filterNot.${column}`, values.join(","))
      }
    }
  }

  // Optional projection — serialize as columns=col1,col2. Only sent when a
  // non-empty projection is requested so we never send an empty `columns` (the
  // backend treats an empty value as a client error rather than "all columns").
  if (params.columns !== undefined && params.columns.length > 0) {
    searchParams.set("columns", params.columns.join(","))
  }

  const url = `${baseUrl}/all?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    )
  }

  const data: RoutineSurveillanceBulkResponse = await response.json()

  // Validate the response shape rather than trusting it blindly — a malformed
  // response should fail loudly here instead of feeding undefined into the
  // graph's grouping/plotting downstream.
  if (!Array.isArray(data?.items)) {
    throw new Error(
      "Unexpected routine surveillance bulk API response: missing 'items' array."
    )
  }

  return data
}
